import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Button, InputField, Label, Modal, Switch } from '../../../../Components/uielements';
import DropDown from '../../../../Components/uielements/dropDown';
import {
  ModalButtonWrapper,
  ModalGap,
  OutlineButton,
  SwitcherLabel,
} from '../../../../services/settings/appStyle/global.style';
import { IntlMessages } from '../../../../helper/langHelper';
import {
  PermissionRowWrapper,
  PermissionLabel,
  PermissionActions,
  PermissionAction,
  TooltipText,
  ThreeColumns,
} from '../../SubscriptionPlanV2/Subscription.style';
import {
  createCustomRole,
  updateCustomRole,
} from '../../../../services/bioplus-services/customeRoles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getDefaultPermissions } from '../../../../services/bioplus-services/subscription-plan';
import { ModalWrapperNew } from '../../UserV2/user.style';

const ROLE_ACCESS_OPTIONS = [
  { label: 'Mobile', value: 'M' },
  { label: 'Desktop', value: 'D' },
  { label: 'Both', value: 'B' },
];

const SECURE_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const isPermissionToggleable = (permission) => {
  if (!permission) return false;
  return (
    permission.isView !== null ||
    permission.isCreate !== null ||
    permission.isManage !== null ||
    permission.isReport !== null
  );
};

const readIsCreate = (source) => {
  if (!source) return null;
  if (source.isCreate !== undefined) return source.isCreate;
  if (source.isCraete !== undefined) return source.isCraete; // tolerate known API typo
  return null;
};

const buildInitialPermissions = (defaultPermissions = [], existingPermissions = []) => {
  const existingByLabel = existingPermissions.reduce((acc, perm) => {
    if (perm?.label) acc[perm.label] = perm;
    return acc;
  }, {});

  return defaultPermissions.map((permission) => {
    const existing = existingByLabel[permission.label];
    const source = existing || permission;
    const toggleable = isPermissionToggleable(permission);

    return {
      label: permission.label,
      description: permission.description,
      isView: toggleable ? !!source.isView : null,
      isReport: toggleable ? !!source.isReport : null,
      isManage: toggleable ? !!source.isManage : null,
      isCreate: toggleable ? !!readIsCreate(source) : null,
      // Not editable here — carried straight through.
      limit: permission.limit ?? null,
      range: permission.range ?? null,
    };
  });
};

// Edit mode already receives the role's full `permission` array from
// roleData (correct labels/descriptions/limit/range and the actual
// true/false/null flags) — no need to merge against getDefaultPermissions.
const normalizeExistingPermissions = (existingPermissions = []) => {
  return existingPermissions.map((permission) => {
    const toggleable = isPermissionToggleable(permission);

    return {
      label: permission.label,
      description: permission.description,
      isView: toggleable ? !!permission.isView : null,
      isReport: toggleable ? !!permission.isReport : null,
      isManage: toggleable ? !!permission.isManage : null,
      isCreate: toggleable ? !!readIsCreate(permission) : null,
      limit: permission.limit ?? null,
      range: permission.range ?? null,
    };
  });
};

const CustomRolePermissionRow = ({ permission, index, onToggle, disabled }) => {
  const toggleable = isPermissionToggleable(permission);
  if (!toggleable) return null;

  return (
    <>
      {permission.description && (
        <TooltipText style={{ display: 'flex', gap: '5px' }}>
          <InfoOutlinedIcon /> {permission.description}
        </TooltipText>
      )}
      <PermissionRowWrapper>
        <PermissionLabel>{permission.label}</PermissionLabel>

        <PermissionActions>
          {['isView', 'isCreate', 'isManage', 'isReport'].map((field) => (
            <PermissionAction key={field}>
              <Switch
                handleToggle={() => onToggle(index, field)}
                checked={!!permission[field]}
                disabled={disabled}
              />
              <SwitcherLabel>
                {field === 'isView' && <IntlMessages id="text.feature.view" defaultValue="View" />}
                {field === 'isCreate' &&
                  (permission.label === 'user' ? (
                    <IntlMessages id="text.feature.invite" defaultValue="Invite" />
                  ) : (
                    <IntlMessages id="text.feature.create" defaultValue="Create" />
                  ))}
                {field === 'isManage' && (
                  <IntlMessages id="text.feature.manager" defaultValue="Manage" />
                )}
                {field === 'isReport' && (
                  <IntlMessages id="text.feature.report" defaultValue="Report" />
                )}
              </SwitcherLabel>
            </PermissionAction>
          ))}
        </PermissionActions>
      </PermissionRowWrapper>
    </>
  );
};

const CreateAndUpdateCustomRoleModal = ({ onClose, isEdit, roleData, companyId }) => {
  const [label, setLabel] = useState(roleData?.label || '');
  const [roleAccess, setRoleAccess] = useState(roleData?.roleAccess || 'B');
  const [secure, setSecure] = useState(
    roleData?.secure === 1 || roleData?.secure === true || false
  );

  const [permissions, setPermissions] = useState(
    isEdit ? normalizeExistingPermissions(roleData?.permission ?? []) : []
  );

  const queryClient = useQueryClient();

  // Only needed for create — edit mode already has full permission data on roleData.
  const defaultPermissionsQuery = useQuery(['defaultPermissions'], () => getDefaultPermissions(), {
    enabled: !isEdit,
  });
  const defaultPermissions = defaultPermissionsQuery?.data?.permissions?.defaultPermissions ?? [];

  useEffect(() => {
    if (isEdit) {
      setPermissions(normalizeExistingPermissions(roleData?.permission ?? []));
      return;
    }

    if (defaultPermissions.length > 0) {
      setPermissions(buildInitialPermissions(defaultPermissions, []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, roleData, defaultPermissionsQuery?.data]);

  const handleToggle = (index, field) => {
    setPermissions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: !next[index][field] };
      return next;
    });
  };

  const handleRoleAccessChange = (option) => {
    setRoleAccess(option?.value ?? option);
  };

  const handleSecureChange = (option) => {
    setSecure(option?.value ?? option);
  };

  const mutation = useMutation(
    (payload) => (isEdit ? updateCustomRole(payload) : createCustomRole(payload)),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('customRoles');
        await queryClient.invalidateQueries('allCustomRoles');
        alert(isEdit ? 'Role updated successfully!' : 'Role created successfully!');
        onClose();
      },
      onError: (error) => {
        alert(`Error: ${error?.response?.data?.message || 'Something went wrong.'}`);
      },
    }
  );

  const handleSubmit = () => {
    if (!label?.trim()) {
      alert('Please enter a role label.');
      return;
    }

    const payload = {
      ...(!isEdit && { companyId }),
      ...(!isEdit && { label: label.trim() }),
      roleAccess,
      secure: secure ? 1 : 0,
      permissions,
      ...(isEdit && { customRoleId: roleData?._id }),
    };

    mutation.mutate(payload);
  };

  const isLoadingDefaults = !isEdit && defaultPermissionsQuery.isLoading;

  return (
    <Modal isOpen={true} onClose={onClose} title={isEdit ? 'Edit Role' : 'Add Role'}>
      <ModalWrapperNew>
        {isLoadingDefaults ? (
          <Label>Loading permissions...</Label>
        ) : (
          <>
            <ModalGap>
              <ThreeColumns>
                <InputField
                  Name="Label"
                  name="label"
                  fullWidth
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  disabled={isEdit}
                />

                <DropDown
                  selectedOption={handleRoleAccessChange}
                  options={ROLE_ACCESS_OPTIONS}
                  name="roleAccess"
                  label="Access"
                  defaultValue={
                    ROLE_ACCESS_OPTIONS.find((option) => option.value === roleAccess)?.label
                  }
                />

                <DropDown
                  selectedOption={handleSecureChange}
                  options={SECURE_OPTIONS}
                  name="secure"
                  label="Secure"
                  defaultValue={SECURE_OPTIONS.find((option) => option.value === secure)?.label}
                />
              </ThreeColumns>
            </ModalGap>

            <div style={{ display: 'grid', gap: '20px', paddingTop: '20px' }}>
              {permissions.map((permission, index) => (
                <CustomRolePermissionRow
                  key={permission.label ?? index}
                  permission={permission}
                  index={index}
                  onToggle={handleToggle}
                  disabled={mutation.isLoading}
                />
              ))}
            </div>

            <ModalButtonWrapper style={{ paddingTop: '20px' }}>
              <OutlineButton onClick={onClose}>
                <IntlMessages id="modal.button.cancel" defaultValue="Cancel" />
              </OutlineButton>
              <Button varient="normal" onClick={handleSubmit} disabled={mutation?.isLoading}>
                {isEdit ? (
                  <IntlMessages id="button.text.update" defaultValue="Update" />
                ) : (
                  <IntlMessages id="modal.button.submit" defaultValue="Submit" />
                )}
                {mutation?.isLoading ? '...' : ''}
              </Button>
            </ModalButtonWrapper>
          </>
        )}
      </ModalWrapperNew>
    </Modal>
  );
};

export default CreateAndUpdateCustomRoleModal;
